/**
 * Dot Component
 *
 * A versatile dot/indicator component for various use cases:
 * - OTP placeholders
 * - Step indicators
 * - Status badges
 * - Pagination dots
 * - Loading indicators
 *
 * @example Basic usage
 * ```tsx
 * <Dot />
 * <Dot size="large" variant="active" />
 * ```
 *
 * @example Step indicator
 * ```tsx
 * {steps.map((step, i) => (
 *   <Dot key={i} variant={currentStep === i ? 'active' : 'default'} />
 * ))}
 * ```
 */
import React from 'react';
import { cn } from '../../utils/cn';
import styles from './dot.module.css';
import type { DotProps } from './dot.types';

export const Dot: React.FC<DotProps> = ({
    size = 'medium',
    variant = 'default',
    className,
    showIf = true,
    ...props
}) => {
    if (!showIf) return null;

    const dotClasses = cn(
        styles.dot,

        // Size
        size === 'small' && styles.sizeSmall,
        size === 'medium' && styles.sizeMedium,
        size === 'large' && styles.sizeLarge,

        // Variant
        variant === 'default' && styles.variantDefault,
        variant === 'active' && styles.variantActive,
        variant === 'success' && styles.variantSuccess,
        variant === 'error' && styles.variantError,
        variant === 'muted' && styles.variantMuted,

        // Custom className
        className
    );

    return <div className={dotClasses} {...props} />;
};

/**
 * @deprecated Use Dot instead
 */
export const OtpDot = Dot;
